import axiosClient from "utils/axios";
import { commaSeparatedEnums, positiveInt } from "utils/list-query";
import { ApiList, ApiOptionList, ApiResponse } from "../../../types/api.types";
import { PettyWorkStatus } from "features/PettyWorks/constants/petty-work.constants";
import {
  AddPettyWorkRequest,
  PettyWork,
  PettyWorkAssignmentSummary,
  PettyWorkBoqLinePayload,
  UpdatePettyWorkRequest,
} from "features/PettyWorks/types/petty-work.types";
import { Employee } from "features/Employees/types/employee.types";

const PETTY_WORK_LIST_STATUSES = Object.values(PettyWorkStatus);

function normalizePettyWorkListRow(row: PettyWork): PettyWork {
  const r = row as unknown as Record<string, unknown>;
  const raw = r.assignments ?? r.workAssignments ?? r.work_assignments;
  const arr = Array.isArray(raw) ? raw : [];
  if (!arr.length) return row;

  const assignments: PettyWorkAssignmentSummary[] = arr.map((item) => {
    const a = item;
    const u = a.user;
    return {
      id: a.id,
      toUserId: a.toUserId,
      status: a.status ?? null,
      isActive: a.isActive,
      actualEndDate: a.actualEndDate ?? null,
      deleted_at: a.deleted_at ?? null,
      user: u
        ? {
            id: u.id,
            firstName: u.firstName,
            lastName: u.lastName,
            email: u.email,
            avatar: u.avatar,
            employee: u.employee as Employee,
          }
        : null,
    };
  });

  return { ...row, assignments };
}

export type GetPettyWorksListParams = {
  page?: number;
  limit?: number;
  search?: string;
  status?: string | string[];
  clientId?: number | string;
  priorityId?: number | string;
  sourceChannelId?: number | string;
};

export const getPettyWorkOptions = async ({
  query,
  limit,
  page,
  status,
}: {
  query?: string;
  limit?: number;
  page?: number;
  status?: string;
} = {}) => {
  try {
    const response = await axiosClient.get<ApiResponse<ApiOptionList>>(
      `/petty-works/options`,
      {
        params: {
          query,
          limit,
          page,
          status: status || undefined,
        },
      },
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching Petty Work options:", err);
    throw err;
  }
};

export const getPettyWorks = async ({
  page,
  limit,
  search,
  status,
  clientId,
  priorityId,
  sourceChannelId,
}: GetPettyWorksListParams = {}) => {
  try {
    const response = await axiosClient.get<ApiResponse<ApiList<PettyWork[]>>>(
      "/petty-works",
      {
        params: {
          page,
          limit,
          search: search?.trim() || undefined,
          status: commaSeparatedEnums(status, PETTY_WORK_LIST_STATUSES),
          clientId: positiveInt(clientId),
          priorityId: positiveInt(priorityId),
          sourceChannelId: positiveInt(sourceChannelId),
        },
      },
    );
    const body = response.data;
    if (body.success && body.data && Array.isArray(body.data.rows)) {
      return {
        ...body,
        data: {
          ...body.data,
          rows: body.data.rows.map((row) => normalizePettyWorkListRow(row)),
        },
      };
    }
    return body;
  } catch (err) {
    console.warn("Error fetching petty-works : ", err);
    throw err;
  }
};

export const createPettyWork = async (payload: AddPettyWorkRequest) => {
  try {
    const response = await axiosClient.post<ApiResponse<PettyWork>>(
      "/petty-works",
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error creating Petty Work : ", err);
    throw err;
  }
};

export const updatePettyWork = async (
  id: number,
  payload: UpdatePettyWorkRequest,
) => {
  try {
    const response = await axiosClient.put<ApiResponse<PettyWork>>(
      `/petty-works/${id}`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error updating Petty Work : ", err);
    throw err;
  }
};


export const getPettyWork = async (id: number) => {
  try {
    if (!Number.isInteger(id) || id <= 0) {
      throw new Error("Invalid ID");
    }
    const response = await axiosClient.get<ApiResponse<PettyWork>>(
      `/petty-works/${id}`,
    );
    const body = response.data;
    if (body.success && body.data) {
      return { ...body, data: normalizePettyWorkListRow(body.data) };
    }
    return body;
  } catch (err) {
    console.warn("Error fetching Petty Work : ", err);
    throw err;
  }
};

export const deletePettyWork = async (id: number) => {
  try {
    const response = await axiosClient.delete<ApiResponse<PettyWork>>(
      `/petty-works/${id}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error deleting Petty Work : ", err);
    throw err;
  }
};

export const cancelPettyWork = async (
  id: number,
  payload?: { reason?: string | null },
) => {
  try {
    const response = await axiosClient.post<ApiResponse<PettyWork>>(
      `/petty-works/${id}/cancel`,
      payload ?? {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error cancelling Petty Work : ", err);
    throw err;
  }
};

/** APPROVED → IN_PROGRESS (requires linked work order; idempotent if already in progress). */
export const startPettyWork = async (id: number) => {
  try {
    const response = await axiosClient.post<ApiResponse<PettyWork>>(
      `/petty-works/${id}/start`,
      {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error starting Petty Work : ", err);
    throw err;
  }
};

/** IN_PROGRESS → ON_HOLD */
export const holdPettyWork = async (
  id: number,
  payload?: { reason?: string | null },
) => {
  try {
    const response = await axiosClient.post<ApiResponse<PettyWork>>(
      `/petty-works/${id}/hold`,
      payload ?? {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error holding Petty Work : ", err);
    throw err;
  }
};

/** ON_HOLD → IN_PROGRESS */
export const resumePettyWork = async (id: number) => {
  try {
    const response = await axiosClient.post<ApiResponse<PettyWork>>(
      `/petty-works/${id}/resume`,
      {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error resuming Petty Work : ", err);
    throw err;
  }
};

/**
 * IN_PROGRESS → READY_FOR_BILLING. Gated by petty_work.approve.
 * Also blocks if the petty work has an open Material Request.
 */
export const approvePettyWorkForBilling = async (
  id: number,
  payload?: { notes?: string | null },
) => {
  try {
    const response = await axiosClient.post<ApiResponse<PettyWork>>(
      `/petty-works/${id}/approve-for-billing`,
      payload ?? {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error approving petty work for billing : ", err);
    throw err;
  }
};

/**
 * READY_FOR_BILLING → COMPLETED. Gated by petty_work.submit_billing.
 * Generates the invoice from the BOQ items and settles any linked client advance.
 * `finalAmount`, if given, overrides the agreed amount — the invoice adds an
 * adjustment line when it differs from the items total.
 */
export const submitPettyWorkBilling = async (
  id: number,
  payload: { finalAmount?: number | null } = {},
) => {
  try {
    const response = await axiosClient.post<ApiResponse<PettyWork>>(
      `/petty-works/${id}/submit-billing`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error submitting petty work billing : ", err);
    throw err;
  }
};

/**
 * Corrects billing for this petty work's `finalAmount`. If no active invoice
 * currently exists, a new one is generated. If the active invoice already has
 * a payment or advance settlement applied, that same invoice's total is
 * corrected in place instead (same id/code, only its adjustment line and
 * total change — re-fetch invoices for this source afterward either way).
 * Requires COMPLETED. Gated by petty_work.regenerate_invoice. Rejected if an
 * active invoice exists with nothing collected against it yet — cancel it
 * first via {@link cancelInvoice} (`features/Invoices/services/InvoiceService`) —
 * or if the new amount is less than what's already been collected.
 *
 * `items`, when provided, replaces the petty work's own stored line items
 * (not just a one-off override on the invoice) — this is Accountant/
 * Marketing's only path to correct line items on a COMPLETED petty work,
 * since Post-Completion Editing otherwise restricts that to Super Admin.
 * Omit it to leave existing items untouched. Note: in the in-place-correction
 * case, this only updates the petty work's own stored items — it does not
 * retroactively rewrite the already-issued invoice's other line items.
 */
export const regeneratePettyWorkInvoice = async (
  id: number,
  payload: {
    finalAmount: number;
    reason?: string | null;
    items?: PettyWorkBoqLinePayload[];
  },
) => {
  try {
    const response = await axiosClient.post<ApiResponse<PettyWork>>(
      `/petty-works/${id}/regenerate-invoice`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error regenerating petty work invoice : ", err);
    throw err;
  }
};
